import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import XLSX from "xlsx-js-style";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";
import { domain } from "../../../security";
import {
  X,
  Check,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Trash2,
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  Filter,
  CheckCircle2,
  AlertOctagon,
  Info,
  ArrowRight,
  Tag,
} from "lucide-react";

const STORAGE_KEY = "bulk_product_session_group";
const MAX_ROWS = 5000;

// =============================================================================
// DROPZONE
// =============================================================================
const DropZone = ({ onFile, isUploading }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files?.[0]) onFile(e.dataTransfer.files[0]);
  };

  return (
    <div
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !isUploading && fileRef.current?.click()}
      className={`relative w-full rounded-2xl border-2 border-dashed p-12 flex flex-col items-center cursor-pointer transition-all duration-200 ${
        isUploading
          ? "border-indigo-300 bg-indigo-50/50 cursor-wait"
          : isDragging
            ? "border-indigo-500 bg-indigo-50 scale-[1.01]"
            : "border-slate-200 hover:border-indigo-400 hover:bg-slate-50/60 bg-white"
      }`}
    >
      <div
        className={`p-4 rounded-2xl mb-4 transition-all ${isUploading || isDragging ? "bg-indigo-100" : "bg-slate-100"}`}
      >
        {isUploading ? (
          <RefreshCw className="text-indigo-500 animate-spin" size={32} />
        ) : (
          <FileSpreadsheet
            className={isDragging ? "text-indigo-600" : "text-slate-400"}
            size={32}
          />
        )}
      </div>
      <p className="text-base font-extrabold text-slate-700 mb-1">
        {isUploading ? "Validating your file…" : "Drop your Excel file here"}
      </p>
      <p className="text-sm text-slate-400 mb-4">
        {isUploading ? "Checking names, codes & lookups" : "or click to browse"}
      </p>
      {!isUploading && (
        <span className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 text-xs font-medium text-slate-400 shadow-sm">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          .xlsx &nbsp;·&nbsp; .xls &nbsp;·&nbsp; max {MAX_ROWS.toLocaleString()}{" "}
          rows
        </span>
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          onFile(e.target.files?.[0]);
          e.target.value = null; // <--- THIS IS THE MAGIC FIX
        }}
        disabled={isUploading}
      />
    </div>
  );
};

// =============================================================================
// EXPANDABLE DETAIL ROW
// =============================================================================
const DetailRow = ({ row, index, isSelected, onSelect }) => {
  const errors = row.validationNotes?.errors || [];
  const warnings = row.validationNotes?.warnings || [];
  const isErr = row.validationStatus === "Error";
  const isWarn = row.validationStatus === "Warning";
  const hasDetail = errors.length > 0 || warnings.length > 0;

  return (
    <>
      <tr
        onClick={() => onSelect(isSelected ? null : index)}
        className={`cursor-pointer transition-colors border-b border-slate-50 ${
          isSelected
            ? "bg-indigo-50/50"
            : isErr
              ? "bg-red-50/20   hover:bg-red-50/40"
              : isWarn
                ? "bg-amber-50/20 hover:bg-amber-50/40"
                : "hover:bg-slate-50/70"
        }`}
      >
        <td className="pl-4 pr-2 py-3 w-8 text-slate-300">
          {hasDetail ? (
            isSelected ? (
              <ChevronDown size={13} className="text-indigo-500" />
            ) : (
              <ChevronRight size={13} />
            )
          ) : (
            <span className="w-3.5 inline-block" />
          )}
        </td>
        <td className="px-2 py-3 w-10 mono text-[11px] text-slate-400">
          {row.rowNumber ?? index + 1}
        </td>
        <td className="px-3 py-3">
          <div className="font-bold text-slate-800 text-sm leading-tight">
            {row.bomProductName || (
              <span className="text-red-400 italic text-xs">Missing</span>
            )}
          </div>
        </td>
        <td className="px-3 py-3">
          <span className="mono text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded border border-slate-200">
            {row.description || <span className="text-red-400">—</span>}
          </span>
        </td>
        <td className="px-3 py-3 text-xs text-slate-500 hidden lg:table-cell">
          {row.componentProductName || <span className="text-slate-300">—</span>}
        </td>
        <td className="px-3 py-3 text-xs text-slate-500 hidden xl:table-cell">
          {row.uomName || <span className="text-slate-300">—</span>}
        </td>
        <td className="px-3 py-3 text-xs text-slate-500 hidden xl:table-cell">
          {row.quantity || <span className="text-slate-300">0</span>}
        </td>
      </tr>

      {isSelected && hasDetail && (
        <tr
          className={`border-b border-slate-50 ${isErr ? "bg-red-50/20" : "bg-amber-50/20"}`}
        >
          <td colSpan={7} className="px-4 pb-5 pt-2">
            <div className="ml-10 space-y-3">
              {/* Field summary grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2 mb-1">
                {[
                  ["Output Product (BOM)", row.bomProductName],
                  ["Description", row.description],
                  ["Item (Regular)", row.componentProductName],
                  ["Unit", row.uomName],
                  ["Qty", row.quantity],
                ]
                  .filter(([, v]) => v)
                  .map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-white rounded-lg px-3 py-2 border border-slate-100 min-w-0"
                    >
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-0.5 truncate">
                        {label}
                      </p>
                      <p
                        className="text-xs font-semibold text-slate-700 truncate"
                        title={value}
                      >
                        {value}
                      </p>
                    </div>
                  ))}
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                {errors.length > 0 && (
                  <div className="flex-1 bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-2.5 flex items-center gap-1.5">
                      <AlertOctagon size={12} /> {errors.length} Error
                      {errors.length > 1 ? "s" : ""} — Row will be skipped
                    </p>
                    <ul className="space-y-1.5">
                      {errors.map((e, ei) => (
                        <li
                          key={ei}
                          className="flex items-start gap-2 text-xs text-red-700 bg-white/60 rounded-lg px-3 py-2 border border-red-100"
                        >
                          <ArrowRight
                            size={10}
                            className="mt-0.5 shrink-0 text-red-400"
                          />
                          {e}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {warnings.length > 0 && (
                  <div className="flex-1 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-2.5 flex items-center gap-1.5">
                      <AlertTriangle size={12} /> {warnings.length} Warning
                      {warnings.length > 1 ? "s" : ""} — Row will still be added
                    </p>
                    <ul className="space-y-1.5">
                      {warnings.map((w, wi) => (
                        <li
                          key={wi}
                          className="flex items-start gap-2 text-xs text-amber-800 bg-white/60 rounded-lg px-3 py-2 border border-amber-100"
                        >
                          <ArrowRight
                            size={10}
                            className="mt-0.5 shrink-0 text-amber-400"
                          />
                          {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// =============================================================================
// MAIN MODAL
// =============================================================================
const BulkBOMProductUpload = ({
  isOpen,
  onClose,
  onSuccess,
  initialRestoreId,
}) => {
  const currentUserID = useSelector(selectUserID);

  const [view, setView] = useState("upload");
  const [isUploading, setIsUploading] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [pastSessions, setPastSessions] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filterStatus, setFilterStatus] = useState("All");
  const [uploadLabel, setUploadLabel] = useState("");
  const [labelError, setLabelError] = useState("");

  const fetchSessions = async (groupId) => {
    try {
      const res = await axios.get(
        `${domain}/api/BulkProductUpload/sessions/${groupId}`,
      );
      setPastSessions(res.data.filter((s) => s.status === "Draft"));
    } catch {
      /* silently skip */
    }
  };

  const loadDraft = async (sessionId) => {
    try {
      const res = await axios.get(
        `${domain}/api/BulkProductUpload/session/${sessionId}/items`,
      );
      setActiveSession({ session: res.data.session, items: res.data.items });
      setView("review");
    } catch {
      toast.error("Could not load draft.");
    }
  };

  // ── Declared before useEffect and wrapped in useCallback so the dep array is satisfied ──
  const handleRestoreSession = useCallback(
    async (sessionId) => {
      try {
        setIsUploading(true);
        const res = await axios.post(
          `${domain}/api/BulkProductUpload/session/${sessionId}/restore`,
        );

        setActiveSession({
          session: {
            id: res.data.sessionId,
            sessionId: res.data.sessionId,
            sessionLabel: res.data.sessionLabel + " (Re-evaluated)",
            status: "Draft",
            okCount: res.data.okCount,
            warningCount: res.data.warningCount,
            errorCount: res.data.errorCount,
          },
          items: res.data.items.map((r) => ({
            ...r,
            validationStatus: r.revalidatedStatus,
            validationNotes: {
              errors: r.errors || [],
              warnings: r.warnings || [],
            },
          })),
        });

        setView("review");

        if (res.data.newConflictsFound > 0)
          toast.warning(
            `${res.data.newConflictsFound} new conflict(s) found since original upload.`,
          );
        else if (res.data.clearedCount > 0)
          toast.success(
            `${res.data.clearedCount} previous error(s) have been cleared!`,
          );
        else toast.info("Re-evaluation complete — no status changes.");
      } catch (err) {
        toast.error(
          err.response?.data?.message || "Could not re-evaluate this session.",
        );
        onClose();
      } finally {
        setIsUploading(false);
      }
    },
    [onClose],
  );

  // ── FIX: use localStorage (survives tab changes); handleRestoreSession in deps ──
  useEffect(() => {
    if (!isOpen) return;
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(STORAGE_KEY, id);
    }

    if (initialRestoreId) {
      handleRestoreSession(initialRestoreId);
    } else {
      setView("upload");
      setActiveSession(null);
      setSelectedRow(null);
      setFilterStatus("All");
      setUploadLabel("");
      setLabelError("");
      fetchSessions(id);
    }
  }, [isOpen, initialRestoreId, handleRestoreSession]);

  const deleteDraft = async (sessionId) => {
    try {
      await axios.delete(
        `${domain}/api/BulkProductUpload/session/${sessionId}`,
      );
      setPastSessions((prev) => prev.filter((s) => s.id !== sessionId));
      toast.success("Draft discarded.");
    } catch {
      toast.error("Could not discard draft.");
    }
  };

  const handleDropFile = (file) => {
    /* if (!uploadLabel.trim()) {
      setLabelError(
        "Please enter a label/name for this upload before selecting a file.",
      );
      return;
    } */
    setLabelError("");
    processFile(file);
  };

  const fetchRegularProduct = async () => {
    try {
      const response = await axios.get(
        `${domain}/api/Products/by-item-type-category?category=Regular`
      );

      return response.data;
    } catch (err) {
      toast.error("Failed to load products.");
      console.error(err);
      return [];
    }
  };

  const fetchBOMProduct = async () => {
    try {
      const response = await axios.get(
        `${domain}/api/Products/by-item-type-category?category=BOM`
      );

      return response.data;
    } catch (err) {
      toast.error("Failed to load products.");
      console.error(err);
      return [];
    }
  };

  const fetchUnitOfMeasurements = async () => {
    try {
      const response = await axios.get(
        `${domain}/api/UnitOfMeasurements`
      );

      return response.data;
    } catch (err) {
      toast.error("Failed to load products.");
      console.error(err);
      return [];
    }
  };

  /* const processFile = async (file) => {
    // 1. Fetch all required data
    const regularProducts = await fetchRegularProduct();
    const bomProducts = await fetchBOMProduct();
    const unitOfMeasurements = await fetchUnitOfMeasurements(); // Fetching UOMs

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { raw: false });

        if (!raw.length) return toast.error("File is empty.");

        // ── FIX: row count guard on the frontend ──
        if (raw.length > MAX_ROWS) {
          toast.error(
            `File too large — maximum ${MAX_ROWS.toLocaleString()} rows. Your file has ${raw.length.toLocaleString()} rows.`,
          );
          return;
        }

        // ── FIX: normalised header mapping (handles casing + spaces) ──
        const items = raw.map((r) => ({
          productName: pick(r, "productName", "product_name", "Product Name"),
          itemCode: pick(r, "itemCode", "item_code", "Item Code"),
          barCode: pick(r, "barCode", "bar_code", "Barcode"),
          description: pick(r, "description", "Description"),
          hasSerial:
            (pick(r, "hasSerial", "has_serial", "Has Serial") ?? "")
              .toString()
              .toUpperCase() === "YES",
          brandName: pick(r, "brandName", "brand_name", "Brand Name"),
          categoryName: pick(r, "categoryName", "category_name", "Category"),
          categoryTwoName: pick(
            r,
            "categoryTwoName",
            "category_two_name",
            "Category Two",
          ),
          categoryThreeName: pick(
            r,
            "categoryThreeName",
            "category_three_name",
            "Category Three",
          ),
          categoryFourName: pick(
            r,
            "categoryFourName",
            "category_four_name",
            "Category Four",
          ),
          categoryFiveName: pick(
            r,
            "categoryFiveName",
            "category_five_name",
            "Category Five",
          ),
          salesUomName: pick(r, "salesUomCode", "sales_uom_code", "Sales UOM"),
          purchaseUomName: pick(
            r,
            "purchaseUomCode",
            "purchase_uom_code",
            "Purchase UOM",
          ),
          baseUomName: pick(r, "baseUomCode", "base_uom_code", "Base UOM"),
        }));

        setIsUploading(true);
        const label =
          uploadLabel.trim() || `Upload – ${new Date().toLocaleDateString()}`;

        const res = await axios.post(
          `${domain}/api/BulkProductUpload/upload/${sgId}`,
          {
            uploadedByUserId: currentUserID,
            sessionLabel: label,
            note: null,
            items,
          },
        );

        setActiveSession({ session: res.data, items: res.data.items });
        setSelectedRow(null);
        setFilterStatus("All");
        setView("review");
      } catch (err) {
        toast.error(err.response?.data?.message || "Upload failed.");
      } finally {
        setIsUploading(false);
      }
    };
    reader.readAsBinaryString(file);
  }; */

  const processFile = async (file) => {
    try {
      // 1. Fetch all required data to resolve IDs
      const [regularProducts, bomProducts, unitOfMeasurements] = await Promise.all([
        fetchRegularProduct(),
        fetchBOMProduct(),
        fetchUnitOfMeasurements()
      ]);

      // Create lookup maps for ID resolution (matching the dropdown format)
      const bomMap = new Map(bomProducts.map(p => [`${p.productName}${p.brandName ? ` | ${p.brandName}` : ""}`.trim(), p.id]));
      const regMap = new Map(regularProducts.map(p => [`${p.productName}${p.brandName ? ` | ${p.brandName}` : ""}`.trim(), p.id]));
      const uomMap = new Map(unitOfMeasurements.map(u => [u.unitName, u.id]));

      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const wb = XLSX.read(evt.target.result, { type: "binary" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const raw = XLSX.utils.sheet_to_json(ws);

          if (!raw.length) return toast.error("File is empty.");

          // 2. Group rows by "Output Product (BOM)"
          const groupedData = {};
          const perLineItem = [];

          raw.forEach((r) => {
            // Accessing keys exactly as they appear in the header row
            const bomName = r["Output Product (BOM)"];
            const description = r["Description"] || "";
            const itemName = r["Item (Regular)"];
            const uomName = r["Unit"];
            const qty = parseFloat(r["Qty"]) || 0;

            if (!bomName || !itemName) return;

            const bomId = bomMap.get(bomName);
            const componentId = regMap.get(itemName);
            const uomId = uomMap.get(uomName);

            if (!bomId) {
              console.warn(`BOM Product not found for: ${bomName}`);
              return;
            }

            let _validationStatus = "Ok";

            if(!componentId || !bomId || !qty ){
              _validationStatus = "Error"
            }

            perLineItem.push({
              bomProductName: bomName,
              description: description,
              componentProductName: itemName,
              uomName: uomName,
              quantity: qty,
              // Adding IDs just in case your review table needs them for highlighting errors
              bomProductId: bomId,
              componentProductId: componentId,
              uomId: uomId,
              validationStatus: _validationStatus
            });

            // If this BOM hasn't been started in our object, initialize it
            if (!groupedData[bomName]) {
              groupedData[bomName] = {
                bomProductId: bomId,
                bomProductName: bomName,
                description: description,
                isActive: true,
                components: [],
                promotedByUserId: currentUserID
              };
            }

            // Push component to the list
            groupedData[bomName].components.push({
              componentProductId: componentId,
              componentProductName: itemName,
              quantity: qty,
              uomId: uomId,
              uomName: uomName,
              sortOrder: groupedData[bomName].components.length,
              notes: ""
            });
          });

          // Convert the grouping object into an array of DTOs
          const finalPayload = Object.values(groupedData);

          if (finalPayload.length === 0) {
            return toast.error("No valid BOM products found in file.");
          }

          setIsUploading(true)
          
          setActiveSession({ session: null, items: perLineItem, finalData: finalPayload });
          setSelectedRow(null);
          setFilterStatus("All");
          setView("review");
          // toast.success("BOMs uploaded successfully!");
          // Update your UI state here (e.g., refresh list, close modal)
        } catch (err) {
          console.error(err);
          toast.error("Failed to parse or upload file.");
        } finally {
          setIsUploading(false);
        }
      };
      reader.readAsBinaryString(file);
    } catch (err) {
      toast.error("Failed to load lookup data.");
      console.error(err);
    }
  };

  const handlePromote = async () => {
    if (!activeSession) return;
    setIsPromoting(true);
    try {
      await axios.post(
        `${domain}/api/ProductBom/upload`,
        activeSession.finalData || [],
      );

      await onSuccess()
      toast.success(`${activeSession.finalData?.length} product(s) uploaded successfully!`);
      handleClose();
    } catch (err) {
      console.error('There was a problem adding data:', err);
      toast.error(err.response?.data?.message || "Failed to upload products.");
    } finally {
      setIsPromoting(false);
    }
  };

  const handleClose = () => {
    setActiveSession(null);
    setView("upload");
    setSelectedRow(null);
    setUploadLabel("");
    setLabelError("");
    onClose();
  };

  const sess = activeSession?.session;
  const canPromote = (sess?.okCount || 0) + (sess?.warningCount || 0) > 0;

  // ── FIX: stabilise allItems with useMemo so the two downstream memos don't
  //         see a new array reference on every render (logical || creates a new []) ──
  const allItems = useMemo(() => activeSession?.items ?? [], [activeSession]);

  const canUpload = !allItems.find(i => i.validationStatus === "Error");

  const counts = useMemo(
    () => ({
      ok: allItems.filter((r) => r.validationStatus === "Ok").length,
      warning: allItems.filter((r) => r.validationStatus === "Warning").length,
      error: allItems.filter((r) => r.validationStatus === "Error").length,
    }),
    [allItems],
  );

  const filteredItems = useMemo(() => {
    if (filterStatus === "All") return allItems;
    return allItems.filter((r) => r.validationStatus === filterStatus);
  }, [allItems, filterStatus]);

  if (!isOpen) return null;

  const filterButtons = [
    {
      key: "All",
      label: "All",
      count: allItems.length,
      cls: "text-slate-600  bg-slate-100  border-slate-200  hover:bg-slate-200",
    },
    {
      key: "Ok",
      label: "Valid",
      count: counts.ok,
      cls: "text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100",
    },
    {
      key: "Warning",
      label: "Warnings",
      count: counts.warning,
      cls: "text-amber-700  bg-amber-50   border-amber-200   hover:bg-amber-100",
    },
    {
      key: "Error",
      label: "Errors",
      count: counts.error,
      cls: "text-red-700    bg-red-50     border-red-200     hover:bg-red-100",
    },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-3">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');
        .bpu-modal { font-family: 'Plus Jakarta Sans', sans-serif; }
        .mono      { font-family: 'JetBrains Mono', monospace; }
      `}</style>

      <div className="bpu-modal bg-white w-full max-w-7xl h-[95vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200">
        {/* ── HEADER ── */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-600 rounded-xl shadow-sm shadow-indigo-200">
              <FileSpreadsheet className="text-white" size={18} />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-slate-900">
                {view === "upload"
                  ? "Bulk Product BOM Upload"
                  : "Review Upload Results"}
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {view === "upload"
                  ? "Name your upload, then drop the Excel file — errors shown before anything saves"
                  : (sess?.sessionLabel ?? "Reviewing staged data")}
              </p>
            </div>
          </div>

          {/* Breadcrumb */}
          {view === "review" && !initialRestoreId && (
            <div className="hidden md:flex items-center gap-2 text-xs mr-4">
              {["Upload", "Review", "Confirm"].map((step, i) => (
                <React.Fragment key={step}>
                  {i > 0 && (
                    <ChevronRight size={12} className="text-slate-300" />
                  )}
                  <span
                    className={`flex items-center gap-1.5 font-bold ${i === 1 ? "text-slate-800" : "text-slate-400"}`}
                  >
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${
                        i === 0
                          ? "bg-slate-200 text-slate-500"
                          : i === 1
                            ? "bg-slate-800 text-white"
                            : "border-2 border-slate-200 text-slate-400"
                      }`}
                    >
                      {i + 1}
                    </span>
                    {step}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}

          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* ── BODY ── */}
        <div className="flex-1 overflow-hidden flex flex-col bg-slate-50/40">
          {/* ══ UPLOAD VIEW ══ */}
          {view === "upload" && (
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-2xl mx-auto flex flex-col gap-5 mt-2">
                {/* Label input */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hidden">
                  <label className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
                    <Tag size={15} className="text-indigo-500" />
                    Upload Label / Name
                    <span className="text-red-400 text-xs font-bold ml-1">
                      Required
                    </span>
                  </label>
                  <input
                    type="text"
                    value={uploadLabel}
                    onChange={(e) => {
                      setUploadLabel(e.target.value);
                      if (e.target.value.trim()) setLabelError("");
                    }}
                    placeholder="e.g. Q3 2025 New Arrivals, Electronics Batch 12…"
                    className={`w-full px-4 py-3 rounded-xl border text-sm font-medium placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition ${
                      labelError
                        ? "border-red-300 bg-red-50/30"
                        : "border-slate-200 bg-slate-50 hover:border-slate-300 focus:bg-white"
                    }`}
                  />
                  {labelError && (
                    <p className="flex items-center gap-1.5 text-xs text-red-600 mt-2 font-medium">
                      <AlertOctagon size={12} />
                      {labelError}
                    </p>
                  )}
                  <p className="text-xs text-slate-400 mt-2">
                    This label appears in Upload History so you can identify
                  </p>
                </div>

                {/* Dropzone */}
                <div
                  className="hidden"
                  onClick={(e) => {
                    if (!uploadLabel.trim()) {
                      e.stopPropagation();
                      setLabelError(
                        "Please enter a label above before selecting a file.",
                      );
                    }
                  }}
                >
                  <DropZone onFile={handleDropFile} isUploading={isUploading} />
                </div>
                
                <div>
                  <DropZone onFile={handleDropFile} isUploading={isUploading} />
                </div>

                {/* Column reference */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                  <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                    <Info size={13} className="text-indigo-500" /> Expected
                    Column Headers
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      { name: "Output Product (BOM)", req: true },
                      { name: "Description", req: false },
                      { name: "Item (Regular)", req: true },
                      { name: "Unit", req: true },
                      { name: "Qty", req: true },
                    ].map(({ name, req }) => (
                      <div
                        key={name}
                        className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-100"
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full shrink-0 ${req ? "bg-red-400" : "bg-slate-300"}`}
                        />
                        <code className="mono text-xs text-slate-700 truncate">
                          {name}
                        </code>
                        {req && (
                          <span className="text-[9px] font-black text-red-500 ml-auto shrink-0">
                            REQ
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Past drafts */}
                {pastSessions.length > 0 && !initialRestoreId && (
                  <div className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm">
                    <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                      <AlertTriangle size={13} className="text-amber-500" />{" "}
                      Unfinished Uploads
                    </h3>
                    <div className="space-y-2">
                      {pastSessions.map((s) => (
                        <div
                          key={s.id}
                          className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100"
                        >
                          <div>
                            <p className="text-sm font-bold text-slate-800">
                              {s.sessionLabel}
                            </p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              {new Date(s.uploadedAt).toLocaleString()} ·{" "}
                              <span className="text-emerald-600">
                                {s.okCount || 0} ok
                              </span>
                              {(s.warningCount || 0) > 0 && (
                                <span className="text-amber-600 ml-1">
                                  {s.warningCount} warnings
                                </span>
                              )}
                              {(s.errorCount || 0) > 0 && (
                                <span className="text-red-600   ml-1">
                                  {s.errorCount} errors
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => deleteDraft(s.id)}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
                            >
                              <Trash2 size={13} />
                            </button>
                            <button
                              onClick={() => loadDraft(s.id)}
                              className="px-4 py-2 text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl transition"
                            >
                              Resume
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══ REVIEW VIEW ══ */}
          {view === "review" && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Summary strip */}
              <div className="px-6 py-4 bg-white border-b border-slate-100 shrink-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-2.5">
                    <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2.5">
                      <CheckCircle2
                        size={16}
                        className="text-emerald-600 shrink-0"
                      />
                      <div>
                        <p className="text-xl font-black text-emerald-700 leading-none">
                          {counts.ok}
                        </p>
                        <p className="text-[11px] text-emerald-600 font-bold mt-0.5">
                          Valid rows
                        </p>
                      </div>
                    </div>
                    {counts.warning > 0 && (
                      <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2.5">
                        <AlertTriangle
                          size={16}
                          className="text-amber-600 shrink-0"
                        />
                        <div>
                          <p className="text-xl font-black text-amber-700 leading-none">
                            {counts.warning}
                          </p>
                          <p className="text-[11px] text-amber-600 font-bold mt-0.5">
                            Warnings (added)
                          </p>
                        </div>
                      </div>
                    )}
                    {counts.error > 0 && (
                      <div className="flex items-center gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">
                        <XCircle size={16} className="text-red-600 shrink-0" />
                        <div>
                          <p className="text-xl font-black text-red-700 leading-none">
                            {counts.error}
                          </p>
                          <p className="text-[11px] text-red-600 font-bold mt-0.5">
                            Errors (skipped)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2.5 shrink-0">
                    {!initialRestoreId && (
                      <button
                        onClick={() => {
                          setView("upload");
                          setSelectedRow(null);
                        }}
                        className="px-4 py-2.5 text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition"
                      >
                        Re-upload
                      </button>
                    )}
                    <button
                      onClick={handlePromote}
                      disabled={!canPromote || isPromoting}
                      className="hidden px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-extrabold rounded-xl transition flex items-center gap-2 text-sm shadow-sm shadow-indigo-200"
                    >
                      {isPromoting ? (
                        <RefreshCw className="animate-spin" size={15} />
                      ) : (
                        <Check size={15} />
                      )}
                      Add {counts.ok + counts.warning} Product
                      {counts.ok + counts.warning !== 1 ? "s" : ""}
                    </button>
                    <button
                      onClick={handlePromote}
                      disabled={!canUpload}
                      className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-extrabold rounded-xl transition flex items-center gap-2 text-sm shadow-sm shadow-indigo-200"
                    >
                      {isPromoting ? (
                        <RefreshCw className="animate-spin" size={15} />
                      ) : (
                        <Check size={15} />
                      )}
                      Add {counts.ok + counts.warning} Product
                      {counts.ok + counts.warning !== 1 ? "s" : ""}
                    </button>
                  </div>
                </div>

                {counts.warning > 0 && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-start gap-2">
                    <Info
                      size={12}
                      className="text-amber-500 mt-0.5 shrink-0"
                    />
                    <p className="text-xs text-amber-800">
                      <strong>Warning rows will still be added</strong> — e.g.
                      duplicate product names or missing optional lookups. Click
                      any row to see details. Only <strong>Error rows</strong>{" "}
                      are skipped.
                    </p>
                  </div>
                )}
              </div>

              {/* Filter bar + table */}
              <div className="flex-1 overflow-hidden flex flex-col px-6 py-4 gap-3">
                <div className="flex items-center justify-between shrink-0 flex-wrap gap-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Filter size={12} className="text-slate-400 mr-0.5" />
                    {filterButtons.map(({ key, label, count, cls }) => (
                      <button
                        key={key}
                        onClick={() => {
                          setFilterStatus(key);
                          setSelectedRow(null);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${cls} ${
                          filterStatus === key
                            ? "ring-2 ring-offset-1 ring-indigo-400 opacity-100"
                            : "opacity-70 hover:opacity-100"
                        }`}
                      >
                        {label}
                        <span className="bg-white/70 rounded-full px-1.5 py-0.5 font-black text-[10px] leading-none">
                          {count}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 hidden sm:block">
                    ↕ Click any row to expand details
                  </p>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col min-h-0">
                  <div className="overflow-y-auto flex-1">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
                        <tr>
                          <th className="pl-4 pr-2 py-3 w-8" />
                          <th className="px-2 py-3 w-10 text-[11px] font-black text-slate-400 uppercase tracking-wider">
                            #
                          </th>
                          <th className="px-3 py-3 text-[11px] font-black text-slate-500 uppercase tracking-wider">
                            Output Product (BOM)
                          </th>
                          <th className="px-3 py-3 text-[11px] font-black text-slate-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-3 py-3 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                            Item (Regular)
                          </th>
                          <th className="px-3 py-3 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden xl:table-cell">
                            Unit
                          </th>
                          <th className="px-3 py-3 text-[11px] font-black text-slate-500 uppercase tracking-wider text-right pr-4">
                            Qty
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredItems.length === 0 ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="py-16 text-center text-slate-400 text-sm"
                            >
                              No rows match this filter.
                            </td>
                          </tr>
                        ) : (
                          filteredItems.map((row, i) => (
                            <DetailRow
                              key={row.id ?? i}
                              row={row}
                              index={i}
                              isSelected={selectedRow === i}
                              onSelect={setSelectedRow}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Table footer */}
                  <div className="bg-slate-50 border-t border-slate-100 px-5 py-2.5 flex items-center justify-between text-xs text-slate-400 shrink-0">
                    <span>
                      Showing{" "}
                      <strong className="text-slate-700">
                        {filteredItems.length}
                      </strong>{" "}
                      of{" "}
                      <strong className="text-slate-700">
                        {allItems.length}
                      </strong>{" "}
                      rows
                    </span>
                    {counts.error > 0 && (
                      <button
                        onClick={() => {
                          setFilterStatus("Error");
                          setSelectedRow(null);
                        }}
                        className="text-red-500 font-bold hover:underline flex items-center gap-1"
                      >
                        <AlertOctagon size={11} /> View {counts.error} error
                        {counts.error > 1 ? "s" : ""}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkBOMProductUpload;
